import { ApiResponse, Pagination } from 'app/shared/models';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';

import { SnackbarType } from '@fuse/services/snackbar';
import { translate } from '@jsverse/transloco';

import { BaseService } from './base.service';

export class BaseListService<T> extends BaseService {
  protected readonly DEFAULT_PAGE_SIZE = 10;
  protected readonly DEFAULT_SORT_ORDER = 'ASC';
  protected endPoint: string;
  protected descriptionField = 'description';
  protected entity: BehaviorSubject<T | null> = new BehaviorSubject(null);
  protected entities: BehaviorSubject<T[] | null> = new BehaviorSubject(null);
  protected pagination: BehaviorSubject<Pagination | null> =
    new BehaviorSubject(null);

  constructor() {
    super();
  }

  get entity$(): Observable<T> {
    return this.entity.asObservable();
  }

  get entities$(): Observable<T[]> {
    return this.entities.asObservable();
  }

  get pagination$(): Observable<Pagination> {
    return this.pagination.asObservable();
  }

  protected cleanFilters(filters: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(filters).filter(
        ([_, value]) => value !== undefined && value !== null && value !== ''
      )
    );
  }

  getAll(
    pagination: Pagination,
    filters: Record<string, any> = {},
    isExporting?: boolean,
    customEndPoint?: string
  ): Observable<ApiResponse<T[]>> {
    const cleanedFilters = this.cleanFilters(filters);

    const queryParams = {
      skip:
        Number(pagination.page) * (pagination.size ?? this.DEFAULT_PAGE_SIZE),
      limit: pagination.size ?? this.DEFAULT_PAGE_SIZE,
      sort: pagination.sort ?? this.descriptionField,
      sortOrder: pagination.sortOrder ?? this.DEFAULT_SORT_ORDER,
      ...cleanedFilters,
    };

    if (isExporting) {
      queryParams.sort = this.descriptionField;
    }

    return this.clientService
      .get<ApiResponse<T[]>>(`/${customEndPoint ?? this.endPoint}`, queryParams)
      .pipe(
        tap(({ success, data, page }) => {
          if (!isExporting) {
            if (success && data) {
              this.entities.next(data);

              this.pagination.next({
                length: page.total,
                size: page.limit,
                page: pagination.page,
                lastPage: Math.ceil(page.total / page.limit),
                startIndex: 0,
                endIndex: 0,
              });
            }
          }
        }),
        catchError(({ success, error }) => {
          this.showMessage(
            SnackbarType.Error,
            translate('messages.get_all_error')
          );

          return of(
            new ApiResponse<T[]>(success, [], error, {
              limit: 10,
              skip: 0,
              total: 0,
            })
          );
        })
      );
  }
}
